import client from "./client";

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.records)) return value.records;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.orders)) return value.orders;
  if (Array.isArray(value?.purchases)) return value.purchases;
  if (Array.isArray(value?.expenses)) return value.expenses;
  return [];
};

const num = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const pick = (row, keys) => keys.find((key) => row?.[key] != null && row?.[key] !== "");

const isToday = (value) => {
  if (!value) return false;
  const today = new Date().toISOString().slice(0, 10);
  return String(value).slice(0, 10) === today;
};

const inDateRange = (row, dateKeys, filters = {}) => {
  const dateKey = pick(row, dateKeys);
  const value = dateKey ? String(row[dateKey] || "").slice(0, 10) : "";
  if (filters.fromDate && (!value || value < filters.fromDate)) return false;
  if (filters.toDate && (!value || value > filters.toDate)) return false;
  return true;
};

const matchesSearch = (row, search) => {
  const needle = String(search || "").trim().toLowerCase();
  if (!needle) return true;
  return Object.values(row || {}).join(" ").toLowerCase().includes(needle);
};

const makeFallbackSection = ({ key, title, rows, dateKeys = [], amountKeys = [], quantityKeys = [], statusKeys = [], filters = {} }) => {
  const safeRows = (Array.isArray(rows) ? rows : []).filter(
    (row) => inDateRange(row, dateKeys, filters) && matchesSearch(row, filters.search)
  );
  const statusMap = new Map();
  let today = 0;
  let amount = 0;
  let quantity = 0;

  safeRows.forEach((row) => {
    const dateKey = pick(row, dateKeys);
    const amountKey = pick(row, amountKeys);
    const quantityKey = pick(row, quantityKeys);
    const statusKey = pick(row, statusKeys);

    if (dateKey && isToday(row[dateKey])) today += 1;
    if (amountKey) amount += num(row[amountKey]);
    if (quantityKey) quantity += num(row[quantityKey]);

    if (statusKey) {
      const label = String(row[statusKey] || "Unknown").trim() || "Unknown";
      statusMap.set(label, (statusMap.get(label) || 0) + 1);
    }
  });

  return {
    key,
    title,
    total: safeRows.length,
    today,
    amount,
    quantity,
    extra: {
      status: Array.from(statusMap.entries()).map(([label, value]) => ({ label, value })).slice(0, 8),
    },
    recent: safeRows.slice(0, 6),
  };
};

const buildFallbackOverview = async (filters = {}) => {
  const endpoints = [
    ["sales", "/sales"],
    ["purchases", "/purchases"],
    ["manufacture", "/manufacture"],
    ["tasks", "/tasks"],
    ["rtoDto", "/rto/orders"],
    ["inventory", "/inventory"],
    ["packaging", "/packaging"],
    ["returns", "/customer-returns"],
    ["settlement", "/settlement-reports/records"],
    ["expenses", "/expenses"],
  ];

  const results = await Promise.allSettled(endpoints.map(([, url]) => client.get(url)));
  const data = Object.fromEntries(
    endpoints.map(([key], index) => [key, results[index].status === "fulfilled" ? toArray(results[index].value.data) : []])
  );

  const sections = [
    makeFallbackSection({
      key: "sales",
      title: "Sales",
      rows: data.sales,
      dateKeys: ["sale_date", "created_at"],
      amountKeys: ["amount", "total_amount"],
      quantityKeys: ["quantity", "qty"],
      statusKeys: ["status", "payment_status"],
      filters,
    }),
    makeFallbackSection({
      key: "purchases",
      title: "Purchases",
      rows: data.purchases,
      dateKeys: ["purchase_date", "date", "created_at"],
      amountKeys: ["total_amount", "amount", "price"],
      quantityKeys: ["quantity", "qty"],
      statusKeys: ["payment_status", "status"],
      filters,
    }),
    makeFallbackSection({
      key: "manufacture",
      title: "Manufacture",
      rows: data.manufacture,
      dateKeys: ["updatedAt", "created_at", "createdAt"],
      amountKeys: ["final_price", "base_price"],
      quantityKeys: ["qty", "quantity"],
      statusKeys: ["status", "damage_status"],
      filters,
    }),
    makeFallbackSection({
      key: "tasks",
      title: "Tasks",
      rows: data.tasks,
      dateKeys: ["created_at", "due_date"],
      statusKeys: ["status"],
      filters,
    }),
    makeFallbackSection({
      key: "rtoDto",
      title: "RTO / DTO",
      rows: data.rtoDto,
      dateKeys: ["dispatch_date", "scanned_at", "created_at"],
      amountKeys: ["amount"],
      quantityKeys: ["qty", "quantity"],
      statusKeys: ["type", "verification", "status"],
      filters,
    }),
    makeFallbackSection({
      key: "inventory",
      title: "Inventory",
      rows: data.inventory,
      dateKeys: ["updated_at", "created_at"],
      amountKeys: ["price"],
      quantityKeys: ["stock"],
      statusKeys: ["category"],
      filters,
    }),
    makeFallbackSection({
      key: "packaging",
      title: "Packaging",
      rows: data.packaging,
      dateKeys: ["entry_date", "created_at"],
      amountKeys: ["total_cost", "amount"],
      quantityKeys: ["quantity", "qty"],
      statusKeys: ["status", "type"],
      filters,
    }),
    makeFallbackSection({
      key: "returns",
      title: "Customer Returns",
      rows: data.returns,
      dateKeys: ["return_date", "created_at"],
      quantityKeys: ["quantity", "qty"],
      statusKeys: ["status", "product_condition"],
      filters,
    }),
    makeFallbackSection({
      key: "settlement",
      title: "Settlement",
      rows: data.settlement,
      dateKeys: ["dispatch_date", "created_at"],
      amountKeys: ["bankSettlement", "bank_settlement", "expectedSettlement", "expected_settlement"],
      statusKeys: ["settlementStatus", "settlement_status"],
      filters,
    }),
    makeFallbackSection({
      key: "expenses",
      title: "Expenses",
      rows: data.expenses,
      dateKeys: ["expense_date", "created_at"],
      amountKeys: ["amount"],
      statusKeys: ["status", "category"],
      filters,
    }),
  ];

  return {
    generatedAt: new Date().toISOString(),
    fallback: true,
    totals: {
      records: sections.reduce((sum, section) => sum + section.total, 0),
      today: sections.reduce((sum, section) => sum + section.today, 0),
      amount: sections.reduce((sum, section) => sum + section.amount, 0),
    },
    sections,
  };
};

export const reportAPI = {
  async getMasterOverview(filters = {}) {
    try {
      const res = await client.get("/reports/master-overview", { params: filters });
      return res.data;
    } catch (error) {
      return buildFallbackOverview(filters);
    }
  },
};
